import { WorkOrderChangeStatusEnum } from 'gql/graphql';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import type { ModalWorkOrderEnum } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { WorkOrderChapters } from 'pages/Project/UerProject/WorkOrder/workorderTypes';

import { useCallback, useMemo, useState } from 'react';

export enum ModalName {
    Other = 'Other',
    Price = 'Price',
    Coefficient = 'Coefficient',
}

export const useAddToWorkOrderChangeMenu = ({
    filters,
    workOrderChangeStatus,
    actionsDisabled,
    openModal,
}: {
    filters: {
        chapter?: WorkOrderChapters;
        objectId?: number;
        subObjectId?: number;
    };
    workOrderChangeStatus: WorkOrderChangeStatusEnum | undefined;
    actionsDisabled: boolean;
    openModal?: (modalType: ModalWorkOrderEnum) => void;
}) => {
    const [openedModal, setOpenedModal] = useState<{ [key: string]: boolean }>({});

    const openModalByName = useCallback((modalName) => {
        setOpenedModal({ [modalName]: true });
    }, []);

    const closeModalsHandler = useCallback(() => {
        setOpenedModal({});
    }, []);
    const workOrderChangeStatusOnFormation = workOrderChangeStatus === WorkOrderChangeStatusEnum.OnFormation;

    const pricesWithTooltip = useMemo(
        () =>
            (workOrderChangeStatusOnFormation &&
                filters?.chapter !== WorkOrderChapters.OTHERS &&
                (!filters?.chapter || !filters?.objectId || !filters?.subObjectId)) ||
            filters?.chapter === WorkOrderChapters.OTHERS,
        [filters?.chapter, filters?.objectId, filters?.subObjectId, workOrderChangeStatusOnFormation],
    );

    const otherWithTooltip = useMemo(
        () => workOrderChangeStatusOnFormation && !(filters?.chapter === WorkOrderChapters.OTHERS && filters?.objectId),
        [filters?.chapter, filters?.objectId, workOrderChangeStatusOnFormation],
    );

    const isPricesBtnDisabled = useMemo(
        () =>
            actionsDisabled ||
            filters?.chapter === WorkOrderChapters.OTHERS ||
            !filters?.subObjectId ||
            !filters?.chapter,
        [actionsDisabled, filters?.chapter, filters?.subObjectId],
    );
    const isOtherBtnDisabled = useMemo(
        () => actionsDisabled || filters?.chapter !== WorkOrderChapters.OTHERS,
        [actionsDisabled, filters?.chapter],
    );

    const isDisabledCoefficient = useMemo(
        () => !workOrderChangeStatusOnFormation || !filters.subObjectId || !filters.chapter,
        [filters.chapter, filters.subObjectId, workOrderChangeStatusOnFormation],
    );

    const menuItems = useMemo(
        () => [
            {
                label: 'Расценку',
                action: () => {
                    openModalByName(ModalName.Price);
                },
                disabled: isPricesBtnDisabled,
                haveTooltip: pricesWithTooltip,
                tooltipName: ModalName.Price,
                tooltipMsg: 'Выберите раздел, объект и подобъект',
            },
            {
                label: 'Коэффициент',
                action: () => {
                    openModalByName(ModalName.Coefficient);
                },
                disabled: isDisabledCoefficient,
                haveTooltip: pricesWithTooltip,
                tooltipName: ModalName.Coefficient,
                tooltipMsg: 'Выберите раздел, объект и подобъект',
            },
            {
                label: 'Прочие',
                action: () => {
                    openModalByName(ModalName.Other);
                },
                disabled: isOtherBtnDisabled,
                haveTooltip: otherWithTooltip,
                tooltipName: ModalName.Other,
                tooltipMsg: 'Выберите раздел "Прочие" и объект',
            },
        ],
        [
            isDisabledCoefficient,
            isOtherBtnDisabled,
            isPricesBtnDisabled,
            openModalByName,
            otherWithTooltip,
            pricesWithTooltip,
        ],
    );

    const {
        getMenuItemAttributes,
        onMenuMouseMove,
        onMenuMouseLeave,
        tooltipPosition,
        activeTooltip,
        activeTooltipMsg,
    } = useDropdownContextMenuTooltipController();

    return {
        menuItems,
        isOpenedModalPrices: openedModal[ModalName.Price],
        isOpenedModalOthers: openedModal[ModalName.Other],
        isOpenedModalCoefficient: openedModal[ModalName.Coefficient],
        closeModalsHandler,
        getMenuItemAttributes,
        onMenuMouseMove,
        onMenuMouseLeave,
        tooltipPosition,
        activeTooltip,
        activeTooltipMsg,
    };
};
